import { createEffect, sample } from 'effector';
import { createBrowserHistory } from 'history';
import type { i18n } from 'i18next';
import { flushRequested, onlineChanged } from '../entities/library';
import {
  $locale,
  $resolvedTheme,
  localeOverridden,
  preferencesLoaded,
  readPreferences,
  systemThemeChanged,
  type ResolvedTheme,
} from '../entities/preferences';
import { loadMethodsFx, sessionCheckRequested } from '../entities/session';
import { createI18n } from '../shared/lib/i18n';
import { linkLocale, type Locale } from '../shared/lib/locale';
import { router } from '../shared/routing';
import './model';

/**
 * Boots the app: loads preferences, creates the i18n instance and wires DOM side effects
 * (data-theme, lang) to the preference stores. Call once, before the first render.
 */
export function initApp(): i18n {
  const preferences = readPreferences(navigator.languages);
  // A link's language (`?lang=bg`) wins for this visit, without replacing the saved preference.
  const fromLink = linkLocale(window.location.search);
  const startLocale = fromLink ?? preferences.locale;
  const i18n = createI18n(startLocale);

  const applyThemeFx = createEffect((theme: ResolvedTheme) => {
    document.documentElement.dataset.theme = theme;
  });
  const applyLocaleFx = createEffect(async (locale: Locale) => {
    document.documentElement.lang = locale;
    await i18n.changeLanguage(locale);
  });

  sample({ clock: $resolvedTheme, target: applyThemeFx });
  sample({ clock: $locale, target: applyLocaleFx });

  const lightQuery = window.matchMedia('(prefers-color-scheme: light)');
  systemThemeChanged(lightQuery.matches ? 'light' : 'dark');
  lightQuery.addEventListener('change', (event) => {
    systemThemeChanged(event.matches ? 'light' : 'dark');
  });

  // Never lose edits: save immediately when the page is hidden or closed.
  const flush = (): void => {
    flushRequested();
  };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });

  // Sync (when signed in): retry uploads when the connection comes back.
  window.addEventListener('online', () => {
    onlineChanged(true);
  });
  window.addEventListener('offline', () => {
    onlineChanged(false);
  });

  router.setHistory(createBrowserHistory());
  sessionCheckRequested();
  void loadMethodsFx();

  preferencesLoaded(preferences);
  if (fromLink) localeOverridden(fromLink);
  void applyThemeFx(
    preferences.theme === 'system' ? (lightQuery.matches ? 'light' : 'dark') : preferences.theme,
  );
  document.documentElement.lang = startLocale;

  return i18n;
}
